// Phase 6.3.1 — Modal primitive.
//
// The canonical centered-overlay dialog. Replaces the three ad-hoc
// modal implementations (DeleteWorkspaceDialog, StepUpAuthModal,
// AddUserModal) — migrated to this primitive in 6.3.2.
//
// Accessibility contract:
//   * role="dialog", aria-modal="true", aria-labelledby → the title
//   * focus moves into the dialog on open (first focusable, else the
//     dialog container)
//   * Tab is trapped inside (focus.ts)
//   * focus returns to the element that was focused before open
//   * Esc closes — UNLESS busy (in-flight submission)
//   * backdrop click closes — UNLESS busy
//
// Rendered through a portal to <body> so it overlays everything
// regardless of where it's mounted in the tree.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { getFocusable, trapTabKey } from "./focus";
import { isTopOverlay, popOverlay, pushOverlay } from "./overlayStack";
import { GLASS_SURFACE } from "./Card";

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: ModalSize;
  /** When true, Esc + backdrop click are disabled (in-flight op). */
  busy?: boolean;
  /** Hide the default close (X) button — e.g. a forced-choice dialog. */
  hideCloseButton?: boolean;
  children?: ReactNode;
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

export function Modal({
  open,
  onClose,
  title,
  size = "md",
  busy = false,
  hideCloseButton = false,
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const instanceId = useRef<symbol>(Symbol("modal")).current;

  // Register/unregister in the shared overlay stack (Modal +
  // SidePanel) so only the topmost overlay reacts to Esc.
  useEffect(() => {
    if (!open) return;
    pushOverlay(instanceId);
    return () => popOverlay(instanceId);
  }, [open, instanceId]);

  // Focus management: move focus in on open, restore on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Defer one frame so the dialog has mounted + laid out.
    const focusables = getFocusable(dialogRef.current);
    (focusables[0] ?? dialogRef.current)?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [open]);

  // Esc to close (capture phase so it wins over ancestor handlers),
  // ignored while busy.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      // Only the topmost overlay reacts to Esc.
      if (e.key === "Escape" && !busy && isTopOverlay(instanceId)) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, { capture: true });
    return () =>
      document.removeEventListener("keydown", onKey, { capture: true });
  }, [open, busy, onClose, instanceId]);

  const onDialogKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    trapTabKey(e, dialogRef.current);
  }, []);

  const onBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && !busy) {
        e.stopPropagation();
        onClose();
      }
    },
    [busy, onClose],
  );

  if (!open) return null;

  return createPortal(
    <div
      data-testid="modal-backdrop"
      onClick={onBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-loose bg-theme-bg-overlay"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="modal"
        data-size={size}
        data-surface="glass"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        className={[
          // Phase 7.0 S2: frosted glass dialog surface + elevation
          // drop-shadow (the backdrop scrim stays opaque-dark below).
          "w-full outline-none rounded-2xl",
          GLASS_SURFACE,
          "shadow-2xl",
          "flex flex-col max-h-[85vh]",
          SIZE_CLASS[size],
        ].join(" ")}
      >
        <div className="flex items-start justify-between gap-default px-loose pt-loose pb-default">
          <h2 id={titleId} data-testid="modal-title" className="text-h3">
            {title}
          </h2>
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              data-testid="modal-close"
              aria-label="Close dialog"
              className="-mr-1 -mt-1 p-tight rounded-lg text-theme-text-tertiary hover:text-theme-text-primary hover:bg-theme-bg-conv-hover transition-colors duration-150 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
            >
              <X className="w-4 h-4" strokeWidth={1.5} aria-hidden />
            </button>
          )}
        </div>
        <div className="px-loose pb-loose overflow-y-auto scrollbar-thin">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
